import React, { useState, useRef, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
} from "@tanstack/react-table";
import { useReactToPrint } from "react-to-print";
import * as XLSX from "xlsx";
import { FaFileExcel, FaPrint, FaSearch } from "react-icons/fa";
import { GrFormNext } from "react-icons/gr";
import { GrFormPrevious } from "react-icons/gr";
// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  brown: "#5c3d11",
  brownDark: "#3e2005",
  orange: "#e07b2a",
  bgHead: "#f0f4f8",    // clean cool-grey thead
  borderHead: "#dde4ec",
  borderCell: "#eef2f7",
  textHead: "#3a5a7a",    // slate blue-grey header labels
  textCell: "#1a1a1a",
  rowHover: "#fdf6ee",
  footerBg: "#faf3eb",    // dark brown — matches header
  footerText: "#201403",    // warm gold text on brown
  font: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
};

// ─── CSS ──────────────────────────────────────────────────────────────────────
const CSS = `



  @media print {
    .crt-toolbar, .crt-pagination { display: none !important; }
    .crt-card { box-shadow: none !important; border: none !important; }
    thead tr th {
      background-color: ${T.brownDark} !important;
      color: #fff !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

function InjectStyles() {
  useEffect(() => {
    const id = "crt-styles-v4";
    if (!document.getElementById(id)) {
      const el = document.createElement("style");
      el.id = id; el.textContent = CSS;
      document.head.appendChild(el);
    }
  }, []);
  return null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function isNumericValue(val) {
  if (val === null || val === undefined || val === "") return false;
  return !isNaN(Number(val)) && String(val).trim() !== "";
}
function isNumericColumn(data, accessorKey) {
  if (!accessorKey) return false;
  const s = data.filter(r => r[accessorKey] != null).slice(0, 3);
  return s.length > 0 && s.every(r => isNumericValue(r[accessorKey]));
}






// ─── Component ────────────────────────────────────────────────────────────────
export default function CommonReactTable({
  columns, data, headerName,
  showPagination = true,
  showFooter = "false",
  align = "right",
  decimal,
  showPrint = true,
  showExcel = true,
  excelFileName,

}) {
  const [globalFilter, setGlobalFilter] = useState("");
  const [hoveredRow, setHoveredRow] = useState(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: showPagination ? 10 : data.length,
  });
  const table = useReactTable({
    data, columns,

    state: { globalFilter, pagination },
    onPaginationChange: setPagination,


    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const rows = table.getRowModel().rows;
  const { pageIndex, pageSize } = pagination;
  const tableRef = useRef(null);

  const handlePrint = useReactToPrint({ contentRef: tableRef, documentTitle: excelFileName });

  const handleExcelExport = () => {
    const eRows = table.getFilteredRowModel().rows;
    const eCols = table.getAllLeafColumns();
    const excelData = eRows.map((row, i) => {
      const rd = { "Sl.No": i + 1 };
      eCols.forEach(col => {
        const h = typeof col.columnDef.header === "string" ? col.columnDef.header : col.id;
        if (h === "Sl.No") return;
        rd[h] = row.original[col.id];
      });
      return rd;
    });
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, `${excelFileName || "export"}.xlsx`);
  };

  function rowsCount(col) {
    let sum = 0;
    const rdata = data?.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);
    if (col?.FooterParamAction) {
      const p = col.Footerparams?.split("-");
      const p1 = rdata?.reduce((s, r) => s + (Number(r[p[0]]) || 0), 0);
      const p2 = rdata?.reduce((s, r) => s + (Number(r[p[1]]) || 0), 0);
      const p3 = rdata?.reduce((s, r) => s + (Number(r[p[2]]) || 0), 0);
      if (col.FooterParamAction === "-") sum = p1 - p2 - p3;
      else if (col.FooterParamAction === "/") sum = parseFloat(((p1 / p2) * 100).toFixed(1));
      else sum = p1 + p2;
    } else {
      sum = rdata?.reduce((s, r) => s + (Number(r[col.accessorKey]) || 0), 0);
    }
    return !isNaN(sum) ? (decimal === "two" ? sum.toFixed(2) : sum) : "";
  }

  const totalRows = table.getFilteredRowModel().rows.length;
  const allHeaders = table.getHeaderGroups();

  const numericColIds = new Set(
    columns
      .filter(col => col.accessorKey && isNumericColumn(data, col.accessorKey))
      .map(col => col.accessorKey)
  );
  const formatValue = (val) => {
    if (val === null || val === undefined || val === "") {
      return "-";
    }
    return val;
  };
  return (
    <>
      <InjectStyles />
      <div>
        <div className="crt-card">

          <div
            className="crt-toolbar"
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div
              className="crt-toolbar-right mb-2"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <div
                className="search-container"

              >
                <FaSearch className="crt-search-icon" style={{ marginRight: "6px" }} />
                <input
                  value={globalFilter ?? ""}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  placeholder="Search..."
                  className="crt-search"
                  style={{
                    border: "none",
                    outline: "none",
                  }}
                />
              </div>

              {showExcel && (
                <button
                  type="button"
                  className="print-tablebtn btn-success btn btn-sm"
                  onClick={handleExcelExport}
                >
                  <FaFileExcel size={14} />
                </button>
              )}

              {showPrint && (
                <button
                  type="button"
                  className="print-tablebtn btn-primary btn btn-sm"
                  onClick={handlePrint}
                >
                  <FaPrint size={14} />
                </button>
              )}
            </div>
          </div>


          {/* Table */}
          <div className="table-responsive">
            <table ref={tableRef} className="newreporttable table table-bordered table-hovered  ">
              <thead >
                {allHeaders.map(hg => (
                  <tr key={hg.id}>
                    {hg.headers.map((header, i) => {
                      const isLast = i === hg.headers.length - 1;
                      const isNum = numericColIds.has(header.column.columnDef.accessorKey);
                      return (
                        <th
                          key={header.id}
                          onClick={header.column.getToggleSortingHandler()}

                        >
                          {header.isPlaceholder ? null : header.column.columnDef.header}
                          {header.column.getIsSorted() === "asc" ? " ▲" : ""}
                          {header.column.getIsSorted() === "desc" ? " ▼" : ""}
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={columns.length}>No Records Found</td></tr>
                ) : (
                  rows.map((row, rowIdx) => (
                    <tr
                      key={row.id}
                      onMouseEnter={() => setHoveredRow(rowIdx)}
                      onMouseLeave={() => setHoveredRow(null)}
                    >
                      {row.getVisibleCells().map((cell, ci) => {
                        const isLast = ci === row.getVisibleCells().length - 1;
                        const colId = cell.column.columnDef.accessorKey;
                        const isNum = numericColIds.has(colId);
                        const isSlNo = cell.column.id === "slNo";
                        const metaAlign = cell.column.columnDef.meta?.style?.textAlign;
                        const resolvedAlign = metaAlign ? metaAlign : isNum ? "right" : "left";

                        return (
                          // <td

                          //   key={cell.id}

                          // >
                          //   {isSlNo ? (
                          //     <div>
                          //       {cell.column.columnDef.cell
                          //         ? cell.column.columnDef.cell(cell.getContext())
                          //         : cell.getValue()}
                          //     </div>
                          //   ) : cell.column.columnDef.cell ? (
                          //     cell.column.columnDef.cell(cell.getContext())
                          //   ) : (
                          //     cell.getValue()
                          //   )}
                          // </td>

                          <td key={cell.id}>
                            {cell.column.columnDef.cell
                              ? formatValue(cell.column.columnDef.cell(cell.getContext()))
                              : formatValue(cell.getValue())}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>

              {showFooter && rows?.length !== 0 && (
                <tfoot className="newreporttable-foot">
                  <tr>
                    {columns.map((col, index) => {
                      const isLast = index === columns.length - 1;
                      const isLabel = col.Footer === "Total";
                      return (
                        <td key={index} >
                          {!col.Footer ? "" : isLabel ? "Total" : rowsCount(col)}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Pagination */}
          {showPagination && (
            <div className="crt-pagination">
              <button

                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}

              ><GrFormPrevious /></button>
              <span>
                Page <strong >{pageIndex + 1}</strong> of{" "}
                <strong >{table.getPageCount()}</strong>
              </span>
              <button

                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}

              ><GrFormNext /></button>
            </div>
          )}

        </div>
      </div>
    </>
  );
}








